import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { CheckCircle, Award, Wrench, Clock, Phone, ArrowRight, HelpCircle, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { features, services as mockServices } from '../data/mock';
import { getServices } from '../services/productApi';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Default FAQ categories (fallback if API is empty)
const defaultFaqCategories = [
  {
    id: 'lieferung',
    title: 'Lieferung & Spedition',
    icon: '🚚',
    questions: [
      { id: '1', question: 'Wie erfolgt die Lieferung von Baumaschinen?', answer: 'Alle unsere Baumaschinen werden per Spedition direkt zu Ihnen geliefert. Die Maschinen sind komplett montiert und sofort einsatzbereit.' },
      { id: '2', question: 'Wie lange dauert die Lieferung?', answer: 'Bei lagernden Artikeln beträgt die Lieferzeit in der Regel 5–10 Werktage innerhalb Deutschlands.' }
    ]
  },
  {
    id: 'garantie',
    title: 'Garantie & Gewährleistung',
    icon: '🛡️',
    questions: [
      { id: '3', question: 'Welche Garantie erhalte ich?', answer: 'Auf alle unsere Baumaschinen gewähren wir eine 12-monatige Jannesmann Qualitätsgarantie ab Kaufdatum.' }
    ]
  }
];

const FAQ = () => {
  const { t } = useTranslation();
  const [services, setServices] = useState(mockServices);
  const [faqCategories, setFaqCategories] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadData = async () => {
      try {
        // Load services
        const servicesData = await getServices();
        if (servicesData?.length > 0) {
          setServices(servicesData);
        }
        
        // Load FAQ categories from API
        const faqRes = await fetch(`${API_URL}/api/faq/categories`);
        if (faqRes.ok) {
          const faqData = await faqRes.json();
          if (faqData.categories && faqData.categories.length > 0) {
            setFaqCategories(faqData.categories);
          } else {
            // Use default categories if API returns empty
            setFaqCategories(defaultFaqCategories);
          }
        } else {
          setFaqCategories(defaultFaqCategories);
        }
      } catch (error) {
        console.error('Error loading data:', error);
        setFaqCategories(defaultFaqCategories);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, []);

  return (
    <div className="min-h-screen bg-white" data-testid="faq-page">
      {/* FAQ Hauptbereich */}
      <section className="py-6 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-2 xl:max-w-[1800px]">
          
          {/* Überschrift */}
          <h1 className="text-lg sm:text-xl font-semibold text-white bg-gray-900 px-6 py-3 rounded-t-lg flex items-center" data-testid="faq-title">
            <HelpCircle className="mr-3" size={24} />
            Häufige Fragen & Antworten (FAQ)
          </h1>

          {/* Intro-Text */}
          <div className="bg-gray-50 border border-gray-200 border-t-0 p-6 md:p-8">
            <p className="text-base sm:text-lg text-gray-700 leading-relaxed mb-6">
              Hier finden Sie Antworten auf die häufigsten Fragen rund um unsere Baumaschinen, Lieferung, Garantie, Finanzierung und Service. 
              Sollte Ihre Frage nicht dabei sein, kontaktieren Sie uns gerne – wir helfen Ihnen persönlich weiter.
            </p>

            {/* Loading State */}
            {loading && (
              <div className="flex justify-center items-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-red-600" />
                <span className="ml-3 text-gray-600">Lade FAQs...</span>
              </div>
            )}

            {/* FAQ Kategorien mit Accordion */}
            {!loading && (
              <div className="space-y-6">
                {faqCategories.map((category) => (
                  <div key={category.id} className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm">
                    {/* Kategorie-Header */}
                    <div className="bg-red-600 text-white px-4 py-3">
                      <h2 className="text-base sm:text-lg font-semibold flex items-center">
                        <span className="mr-3 text-xl">{category.icon}</span>
                        {category.title}
                      </h2>
                    </div>
                    
                    {/* Fragen Accordion */}
                    <Accordion type="single" collapsible className="px-4">
                      {category.questions && category.questions.map((item, idx) => (
                        <AccordionItem 
                          key={item.id || idx} 
                          value={`${category.id}-${item.id || idx}`}
                          className="border-b border-gray-100 last:border-0"
                        >
                          <AccordionTrigger className="text-left py-4 text-sm sm:text-base font-medium text-gray-900 hover:text-red-600 hover:no-underline">
                            {item.question || item.q}
                          </AccordionTrigger>
                          <AccordionContent className="text-sm sm:text-base text-gray-600 leading-relaxed pb-4">
                            {item.answer || item.a}
                          </AccordionContent>
                        </AccordionItem>
                      ))}
                    </Accordion>
                  </div>
                ))}
              </div>
            )}

            {/* Kontakt-Hinweis */}
            <div className="mt-8 bg-gray-100 rounded-xl p-6 border border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900 mb-3">Ihre Frage war nicht dabei?</h3>
              <p className="text-gray-700 mb-4">
                Kontaktieren Sie uns direkt – unser Team steht Ihnen gerne persönlich zur Verfügung:
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <a 
                  href="tel:+4920419877499" 
                  className="inline-flex items-center justify-center bg-red-600 hover:bg-red-700 text-white px-6 py-3 rounded-lg font-medium transition-colors"
                >
                  <Phone className="mr-2" size={18} />
                  02041 9877499
                </a>
                <a 
                  href="mailto:info@jannesmann.de" 
                  className="inline-flex items-center justify-center bg-gray-800 hover:bg-gray-900 text-white px-6 py-3 rounded-lg font-medium transition-colors"
                >
                  <ArrowRight className="mr-2" size={18} />
                  info@jannesmann.de
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Info-Bereich: 4 Vorteile + 3 Leistungen + CTA */}
      <section className="py-6 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-2 xl:max-w-[1800px]">
          <div className="bg-gray-50 rounded-xl p-4 sm:p-6 space-y-4 sm:space-y-6">
            
            {/* 4 Vorteile */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
              {features.map((feature, index) => {
                const IconComponent = {
                  Award,
                  Wrench,
                  Clock,
                  HeadphonesIcon: Phone
                }[feature.icon];
                
                return (
                  <Card key={index} className="border-0 shadow-md hover:shadow-xl transition-shadow duration-300 bg-white">
                    <CardContent className="pt-6 pb-6">
                      <div className="flex flex-col items-center text-center">
                        <div className="w-12 h-12 sm:w-14 sm:h-14 bg-red-100 rounded-full flex items-center justify-center mb-3 sm:mb-4">
                          <IconComponent className="text-red-600" size={24} />
                        </div>
                        <h3 className="font-semibold text-sm sm:text-lg mb-2">{feature.title}</h3>
                        <p className="text-gray-600 text-xs sm:text-sm">{feature.description}</p>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>

            {/* 3 Leistungen */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 md:gap-6">
              {services.map((service) => (
                <Card key={service.id} className="border-0 shadow-md hover:shadow-xl transition-shadow duration-300 bg-white">
                  <CardContent className="pt-6 pb-6">
                    <div className="flex flex-col items-center text-center">
                      <div className="w-12 h-12 sm:w-14 sm:h-14 bg-red-100 rounded-full flex items-center justify-center mb-3 sm:mb-4">
                        {service.id === 1 && <Award className="text-red-600" size={24} />}
                        {service.id === 2 && <Phone className="text-red-600" size={24} />}
                        {service.id === 3 && <Wrench className="text-red-600" size={24} />}
                      </div>
                      <h3 className="font-semibold text-sm sm:text-lg mb-2">{service.title}</h3>
                      <p className="text-gray-600 text-xs sm:text-sm mb-3">{service.description}</p>
                      <ul className="space-y-1 text-left w-full">
                        {service.features.slice(0, 3).map((feat, idx) => (
                          <li key={idx} className="flex items-start text-xs sm:text-sm">
                            <CheckCircle className="text-red-600 mr-2 flex-shrink-0 mt-0.5" size={14} />
                            <span className="text-gray-700">{feat}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* CTA */}
            <Card className="border-0 shadow-md hover:shadow-xl transition-shadow duration-300 bg-white">
              <CardContent className="pt-8 pb-8">
                <div className="flex flex-col items-center text-center">
                  <div className="w-12 h-12 sm:w-14 sm:h-14 bg-red-100 rounded-full flex items-center justify-center mb-3 sm:mb-4">
                    <ArrowRight className="text-red-600" size={24} />
                  </div>
                  <h3 className="font-semibold text-lg sm:text-xl mb-2">{t('cta.readyForProject')}</h3>
                  <p className="text-gray-600 text-sm sm:text-base mb-4 max-w-xl">
                    {t('cta.contactUs')}
                  </p>
                  <Link to="/kontakt">
                    <Button className="bg-red-600 hover:bg-red-700 text-white px-6 py-3 rounded-lg font-medium transition-colors flex items-center">
                      {t('cta.getInTouch')}
                      <ArrowRight className="ml-2" size={20} />
                    </Button>
                  </Link>
                </div>
              </CardContent>
            </Card>

          </div>
        </div>
      </section>

      {/* Zwei Videos */}
      <section className="py-6 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-2 xl:max-w-[1800px]">
          <div className="grid grid-cols-2 gap-4 md:gap-6">
            <div className="relative rounded-xl overflow-hidden shadow-lg">
              <video autoPlay muted loop playsInline preload="metadata" className="w-full h-[140px] sm:h-[220px] md:h-[400px] object-cover" webkit-playsinline="true">
                <source src="/videos/video2.mp4" type="video/mp4" />
              </video>
            </div>
            <div className="relative rounded-xl overflow-hidden shadow-lg">
              <video autoPlay muted loop playsInline preload="metadata" className="w-full h-[140px] sm:h-[220px] md:h-[400px] object-cover" webkit-playsinline="true">
                <source src="/videos/video3.mp4" type="video/mp4" />
              </video>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default FAQ;
